import React from 'react';
import { motion } from 'motion/react';
import { FileText, Download, Calendar, ArrowRight, ShieldCheck, FileSpreadsheet } from 'lucide-react';

interface QefResourceCenterProps {
  onBookConsultation?: () => void;
  t: (key: string) => string;
}

export function QefResourceCenter({ onBookConsultation, t }: QefResourceCenterProps) {
  const resources = [
    { title: t("Space Module QEF Draft"), icon: FileText, color: "text-blue-400" },
    { title: t("IoT Maker Lab Budget Template"), icon: FileSpreadsheet, color: "text-emerald-400" },
    { title: t("Vibe Coding Curriculum Map"), icon: FileText, color: "text-purple-400" },
    { title: t("Drone Missions Risk Assessment"), icon: ShieldCheck, color: "text-orange-400" },
  ];

  return (
    <section className="py-24 px-6 md:px-12 lg:px-24 bg-black relative overflow-hidden border-t border-white/10">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-block px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[12px] font-bold uppercase tracking-[0.2em] mb-6">
            {t("Educator Support")}
          </div>
          <h2 className="text-[40px] md:text-[56px] font-bold leading-tight tracking-tight mb-6 text-white">
            {t("QEF Resource Center")}
          </h2>
          <p className="text-[18px] md:text-[20px] text-white/60 max-w-3xl mx-auto leading-relaxed">
            {t("CreaTech Academy modules are meticulously designed to align with EDB STEAM and Junior Secondary AI Curriculum guidelines. We provide the documentation you need to secure Quality Education Fund (QEF) backing.")}
          </p>
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {resources.map((resource, idx) => (
            <motion.div 
              key={idx}
              whileHover={{ y: -5 }}
              className="bg-titanium border border-silver rounded-2xl p-8 flex flex-col items-start group cursor-pointer hover:border-blue-500/30 transition-colors"
            >
              <div className={`p-4 rounded-xl bg-white/5 mb-6 group-hover:scale-110 transition-transform ${resource.color}`}>
                <resource.icon className="w-8 h-8" />
              </div>
              <h3 className="text-[18px] font-bold text-white leading-snug">
                {resource.title}
              </h3>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-blue-900/40 to-purple-900/40 border border-white/10 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&q=80&w=2000')] opacity-10 mix-blend-overlay" />
          <div className="relative z-10 flex flex-col items-center">
            <Calendar className="w-12 h-12 text-blue-400 mb-6" />
            <h3 className="text-[32px] md:text-[40px] font-bold text-white mb-4">
              {t("Need a Custom Proposal?")}
            </h3>
            <p className="text-[18px] text-white/70 max-w-2xl mx-auto mb-10">
              {t("Schedule a meeting with our curriculum specialists. We'll help you tailor a QEF proposal that perfectly fits your school's unique needs and student demographics.")}
            </p>
            <button onClick={onBookConsultation} className="relative group">
              <div className="absolute inset-0 rounded-full border-[1px] border-blue-500/50 group-hover:border-blue-400 transition-colors" />
              <div className="relative bg-blue-600/20 backdrop-blur-md text-blue-50 rounded-full px-8 py-4 text-[16px] md:text-[18px] font-semibold overflow-hidden shadow-[0_0_15px_rgba(59,130,246,0.3)] group-hover:shadow-[0_0_25px_rgba(59,130,246,0.5)] transition-shadow flex items-center justify-center gap-3">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/0 via-blue-400/10 to-blue-600/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                {t("Book Consultation")}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
